'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { ExternalLink, Zap, Euro, Users, TrendingUp, Leaf, Shield, ArrowRight, CheckCircle, HelpCircle, AlertTriangle, Star, Lock, Cpu, Quote, MapPin } from 'lucide-react';
import styles from '@/styles/public-landing.module.css';
import { AnalyseAnfrageModal } from './AnalyseAnfrageModal';
import { trackLandingPageView, trackCTAClick, trackScrollMilestone } from '@/utils/umami-tracking';

export function PublicLandingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const trackedMilestones = useRef(new Set<number>());

  // Track page view on mount
  useEffect(() => {
    trackLandingPageView();
  }, []);

  // Implement scroll tracking
  useEffect(() => {
    const sections = [
      { selector: `.${styles.valueSection}`, milestone: 25, name: 'value_props' },
      { selector: `.${styles.processSection}`, milestone: 50, name: 'process' },
      { selector: `.${styles.testimonialsSection}`, milestone: 75, name: 'testimonials' },
      { selector: `.${styles.enhancedFooter}`, milestone: 100, name: 'footer' }
    ];

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = sections.find(s => 
            entry.target.matches(s.selector)
          );
          
          if (section && !trackedMilestones.current.has(section.milestone)) {
            trackScrollMilestone(section.milestone, section.name);
            trackedMilestones.current.add(section.milestone);
          }
        }
      });
    }, observerOptions);

    // Start observing sections after a small delay to ensure DOM is ready
    setTimeout(() => {
      sections.forEach(({ selector }) => {
        const element = document.querySelector(selector);
        if (element) {
          observer.observe(element);
        }
      });
    }, 100);

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          {/* Logo */}
          <div className={styles.logoWrapper}>
            <Image 
              src="/logo.svg" 
              alt="Bürgerstrom Logo" 
              width={280} 
              height={112}
              priority
              className={styles.logo}
            />
          </div>
          
          {/* Main Heading */}
          <h1 className={styles.heroTitle}>
            Gemeinsam zur Energiewende
          </h1>
          
          {/* Subtitle */}
          <p className={styles.heroSubtitle}>
            Entdeckt das volle Potenzial eurer Gemeinde – ohne Risiko, ohne Aufwand
          </p>

          {/* CTA Button */}
          <div style={{ marginBottom: '3rem', marginTop: '2rem' }}>
            <button 
              onClick={() => {
                trackCTAClick('hero');
                setIsModalOpen(true);
              }}
              className={styles.primaryButton}
              style={{ fontSize: '1.125rem', padding: '1rem 2.5rem' }}
            >
              <span>Jetzt starten</span>
              <ArrowRight className={styles.buttonIcon} />
            </button>
          </div>

          {/* Key Statistics */}
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>180+</div>
              <div className={styles.statLabel}>Gemeinden</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>-24%</div>
              <div className={styles.statLabel}>Ersparnis</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className={styles.valueSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Was bringt eine Energiegemeinschaft?</h2>
          <div className={styles.valueGrid}>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>
                <Euro className={styles.icon} />
              </div>
              <h3 className={styles.valueTitle}>Für eure Gemeinde</h3>
              <p className={styles.valueText}>
                <strong>Bis zu 24% weniger Stromkosten</strong><br/>
                Bis zu 40.000€ Ersparnis pro Jahr für kommunale Gebäude
              </p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>
                <Users className={styles.icon} />
              </div>
              <h3 className={styles.valueTitle}>Für eure Bürger</h3>
              <p className={styles.valueText}>
                <strong>147-232€ Ersparnis pro Jahr</strong><br/>
                Ohne eigene PV-Anlage von günstigem Gemeinschaftsstrom profitieren
              </p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>
                <Leaf className={styles.icon} />
              </div>
              <h3 className={styles.valueTitle}>Für euer Gewerbe</h3>
              <p className={styles.valueText}>
                <strong>Standortvorteil durch günstige Energie</strong><br/>
                Lokale Unternehmen stärken und Arbeitsplätze sichern
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* How it Works */}
      <section className={styles.processSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>So funktioniert's</h2>
          <div className={styles.processGrid}>
            <div className={styles.processStep}>
              <div className={styles.stepNumber}>1</div>
              <h3 className={styles.stepTitle}>Potenzial analysieren</h3>
              <p className={styles.stepText}>
                Wir analysieren kostenlos das Energie- und Einsparpotenzial eurer Gemeinde
              </p>
            </div>
            <div className={styles.processStep}>
              <div className={styles.stepNumber}>2</div>
              <h3 className={styles.stepTitle}>Gemeinde mobilisieren</h3>
              <p className={styles.stepText}>
                Wir unterstützen euch, Bürger und Betriebe über die Vorteile zu informieren
              </p>
            </div>
            <div className={styles.processStep}>
              <div className={styles.stepNumber}>3</div>
              <h3 className={styles.stepTitle}>EEG gründen</h3>
              <p className={styles.stepText}>
                Wir übernehmen die gesamte Bürokratie der Gründung, ihr könnt euch hier zurücklehnen
              </p>
            </div>
            <div className={styles.processStep}>
              <div className={styles.stepNumber}>4</div>
              <h3 className={styles.stepTitle}>Gemeinsam profitieren</h3>
              <p className={styles.stepText}>
                Ihr spart Kosten und leistet einen Beitrag zum Klimaschutz
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Municipality */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Vorteile für eure Gemeinde</h2>
          <div className={styles.benefitsList}>
            <div className={styles.benefitItem}>
              <CheckCircle className={styles.checkIcon} />
              <div>
                <h4 className={styles.benefitTitle}>Kostenlose Potenzialanalyse</h4>
                <p className={styles.benefitText}>Detaillierte Analyse ohne Verpflichtungen</p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <CheckCircle className={styles.checkIcon} />
              <div>
                <h4 className={styles.benefitTitle}>Professionelle Begleitung</h4>
                <p className={styles.benefitText}>Von der Planung bis zur Umsetzung</p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <CheckCircle className={styles.checkIcon} />
              <div>
                <h4 className={styles.benefitTitle}>Keine Verwaltungskosten</h4>
                <p className={styles.benefitText}>Transparente Preisgestaltung ohne versteckte Gebühren</p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <CheckCircle className={styles.checkIcon} />
              <div>
                <h4 className={styles.benefitTitle}>Langfristige Betreuung</h4>
                <p className={styles.benefitText}>Auch nach der Gründung sind wir für euch da</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ / Bedenken Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Häufige Bedenken & Antworten</h2>
          <div className={styles.faqGrid}>
            <div className={styles.faqCard}>
              <div className={styles.faqIcon}>
                <HelpCircle className={styles.icon} />
              </div>
              <h3 className={styles.faqTitle}>"Ist das nicht zu kompliziert?"</h3>
              <p className={styles.faqText}>
                Nein! Wir übernehmen die komplette Gründung und Verwaltung. 
                Ihr müsst nur entscheiden - den Rest erledigen wir.
              </p>
            </div>
            <div className={styles.faqCard}>
              <div className={styles.faqIcon}>
                <AlertTriangle className={styles.icon} />
              </div>
              <h3 className={styles.faqTitle}>"Was sind die Risiken?"</h3>
              <p className={styles.faqText}>
                Als Initiator tragt ihr Verantwortung für ausreichend Verbrauch in der EEG. 
                Auf Wunsch übernimmt neoom aber die Vereinsobmannschaft und das damit 
                verbundene Risiko.
              </p>
            </div>
            <div className={styles.faqCard}>
              <div className={styles.faqIcon}>
                <Euro className={styles.icon} />
              </div>
              <h3 className={styles.faqTitle}>"Welche Kosten entstehen?"</h3>
              <p className={styles.faqText}>
                Die Gründung ist im Zuge der Sommeraktion kostenlos und die laufende 
                Verwaltung wird über die Servicegebühr durch die Mitglieder getragen.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Comparison Table */}
      <section className={styles.comparisonSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Welche EEG passt zu eurer Gemeinde?</h2>
          <div className={styles.comparisonTable}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <div className={styles.comparisonIcon}>
                  <Lock className={styles.icon} />
                </div>
                <h3 className={styles.comparisonTitle}>Geschlossene EEG</h3>
                <p className={styles.comparisonSubtitle}>Der sichere Einstieg</p>
              </div>
              <div className={styles.comparisonFeatures}>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>20 Teilnehmer</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>25% Eigenverbrauchsquote</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>15% Autarkiequote</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>Gemeindegebäude + Partner</span>
                </div>
              </div>
            </div>
            <div className={`${styles.comparisonCard} ${styles.popular}`}>
              <div className={styles.popularBadge}>
                <Star className={styles.badgeIcon} />
                <span>Beliebteste Wahl</span>
              </div>
              <div className={styles.comparisonHeader}>
                <div className={styles.comparisonIcon}>
                  <Users className={styles.icon} />
                </div>
                <h3 className={styles.comparisonTitle}>Offene EEG</h3>
                <p className={styles.comparisonSubtitle}>Für alle Bürger</p>
              </div>
              <div className={styles.comparisonFeatures}>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>100+ Teilnehmer</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>50% Eigenverbrauchsquote</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>35% Autarkiequote</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>Alle Haushalte der Gemeinde</span>
                </div>
              </div>
            </div>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <div className={styles.comparisonIcon}>
                  <Cpu className={styles.icon} />
                </div>
                <h3 className={styles.comparisonTitle}>Intelligente EEG</h3>
                <p className={styles.comparisonSubtitle}>KI-optimiert</p>
              </div>
              <div className={styles.comparisonFeatures}>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>100+ Teilnehmer</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>75-85% Eigenverbrauchsquote</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>50-60% Autarkiequote</span>
                </div>
                <div className={styles.feature}>
                  <CheckCircle className={styles.featureIcon} />
                  <span>KI-gesteuerte Optimierung</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Success Stories / Testimonials */}
      <section className={styles.testimonialsSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Erfolgsgeschichten aus Tirol</h2>
          <div className={styles.testimonialsGrid}>
            <div className={styles.testimonialCard}>
              <div className={styles.testimonialHeader}>
                <div className={styles.locationBadge}>
                  <MapPin className={styles.locationIcon} />
                  <span>Mils bei Imst</span>
                </div>
                <Quote className={styles.quoteIcon} />
              </div>
              <blockquote className={styles.testimonialText}>
                "Ich kann das jedem Bürgermeister weiterempfehlen. Das Gemeindeamt hat 
                damit nichts zu tun - kein weiteres Risiko, keine Verwaltung."
              </blockquote>
              <div className={styles.testimonialAuthor}>
                <strong>Daniel Pfeifhofer, VZB Mils</strong>
                <span>Erste Energiegemeinschaft Tirols</span>
              </div>
              <div className={styles.testimonialStats}>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>300+</span>
                  <span className={styles.statLabel}>Haushalte</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>0€</span>
                  <span className={styles.statLabel}>Verwaltungsaufwand</span>
                </div>
              </div>
            </div>
            <div className={styles.testimonialCard}>
              <div className={styles.testimonialHeader}>
                <div className={styles.locationBadge}>
                  <MapPin className={styles.locationIcon} />
                  <span>Tiroler Gemeinden</span>
                </div>
                <Quote className={styles.quoteIcon} />
              </div>
              <blockquote className={styles.testimonialText}>
                "Die Bürger sind begeistert: Endlich können sie ohne eigene PV-Anlage 
                von erneuerbarer Energie profitieren und dabei Geld sparen."
              </blockquote>
              <div className={styles.testimonialAuthor}>
                <strong>Bürgerstrom Team</strong>
                <span>180+ EEGs in ganz Österreich</span>
              </div>
              <div className={styles.testimonialStats}>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>147-232€</span>
                  <span className={styles.statLabel}>Ersparnis ohne PV</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>24%</span>
                  <span className={styles.statLabel}>Kostensenkung</span>
                </div>
              </div>
            </div>
            <div className={styles.testimonialCard}>
              <div className={styles.testimonialHeader}>
                <div className={styles.locationBadge}>
                  <MapPin className={styles.locationIcon} />
                  <span>Österreichweit</span>
                </div>
                <Quote className={styles.quoteIcon} />
              </div>
              <blockquote className={styles.testimonialText}>
                "Energiegemeinschaften sind die Zukunft der lokalen Energieversorgung. 
                Sie stärken die Region und schonen das Klima."
              </blockquote>
              <div className={styles.testimonialAuthor}>
                <strong>E-Control Austria</strong>
                <span>Energieregulierungsbehörde</span>
              </div>
              <div className={styles.testimonialStats}>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>1000+</span>
                  <span className={styles.statLabel}>EEGs österreichweit</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>CO₂</span>
                  <span className={styles.statLabel}>Neutral bis 2040</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaCard}>
          <div className={styles.ctaContent}>
            <Shield className={styles.ctaShield} />
            <h2 className={styles.ctaTitle}>
              Entdeckt euer Gemeinde-Potenzial
            </h2>
            <p className={styles.ctaText}>
              Möchtet ihr erfahren, welches Einsparpotenzial eure Gemeinde durch eine 
              Energiegemeinschaft erreichen könnte? Unser Team erstellt gerne eine 
              detaillierte Analyse für eure Gemeinde.
            </p>
            
            <div className={styles.ctaButtonWrapper}>
              <p className={styles.ctaLabel}>
                Bereit für den ersten Schritt?
              </p>
              
              <button 
                onClick={() => {
                  trackCTAClick('footer');
                  setIsModalOpen(true);
                }}
                className={styles.primaryButton}
              >
                <span>Jetzt starten</span>
                <ArrowRight className={styles.buttonIcon} />
              </button>
              
              <p className={styles.ctaNote}>
                Wir freuen uns auf eure Anfrage
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Extended FAQ Section */}
      <section className={styles.extendedFaqSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Häufig gestellte Fragen</h2>
          <div className={styles.extendedFaqGrid}>
            <div className={styles.faqCategory}>
              <h3 className={styles.categoryTitle}>Gründung & Rechtliches</h3>
              <div className={styles.faqItemExpanded}>
                <h4 className={styles.faqQuestion}>Wie lange dauert die Gründung?</h4>
                <p className={styles.faqAnswer}>
                  In der Regel 2 bis 8 Wochen. 
                  Wir übernehmen alle Behördenwege und rechtlichen Schritte.
                </p>
              </div>
              <div className={styles.faqItemExpanded}>
                <h4 className={styles.faqQuestion}>Wie steht es um Datenschutz und Haftung?</h4>
                <p className={styles.faqAnswer}>
                  Eure Daten sind bei uns sicher: DSGVO-konforme Verarbeitung durch 
                  neoom als etablierten Partner.
                </p>
              </div>
            </div>
            <div className={styles.faqCategory}>
              <h3 className={styles.categoryTitle}>Technik & Betrieb</h3>
              <div className={styles.faqItemExpanded}>
                <h4 className={styles.faqQuestion}>Brauchen wir neue Zähler?</h4>
                <p className={styles.faqAnswer}>
                  Smart Meter sind Voraussetzung, aber meist bereits vorhanden. 
                  Falls nicht installiert wird dieser kostenlos vom Netzbetreiber installiert.
                </p>
              </div>
              <div className={styles.faqItemExpanded}>
                <h4 className={styles.faqQuestion}>Was passiert bei Stromausfall?</h4>
                <p className={styles.faqAnswer}>
                  Ehrlich gesagt: Bei Stromausfall geht erstmal nichts mehr - wie überall 
                  im Netz. Für echte Notstromversorgung bräuchte es einen Batteriespeicher 
                  mit Notstromumschaltung, aber das ist eine andere Baustelle.
                </p>
              </div>
            </div>
            <div className={styles.faqCategory}>
              <h3 className={styles.categoryTitle}>Finanzen & Abrechnung</h3>
              <div className={styles.faqItemExpanded}>
                <h4 className={styles.faqQuestion}>Wie wird abgerechnet?</h4>
                <p className={styles.faqAnswer}>
                  Automatische quartalsweise Abrechnung über unsere Software. 
                  Transparente Aufstellung aller Erzeugung und Verbräuche.
                </p>
              </div>
              <div className={styles.faqItemExpanded}>
                <h4 className={styles.faqQuestion}>Was sind die laufenden Kosten?</h4>
                <p className={styles.faqAnswer}>
                  Die Abrechnung erfolgt quartalsweise und ihr erhaltet vorab ein 
                  Muster-Tarifblatt mit allen Details - transparent und maßgeschneidert 
                  für eure Gemeinde.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className={styles.enhancedFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerMain}>
            <div className={styles.footerBrand}>
              <h3 className={styles.footerTitle}>Bürgerstrom Tirol</h3>
              <p className={styles.footerDescription}>
                Ihr Partner für Energiegemeinschaften in Tirol
              </p>
            </div>
            {/* Mobile Contact Section */}
            <div className={styles.footerMobileContact}>
              <a href="tel:051240506070" className={styles.footerContactLink}>
                0512 40 50 60 70
              </a>
              <a href="mailto:kontakt@buergerstrom.tirol" className={styles.footerContactLink}>
                kontakt@buergerstrom.tirol
              </a>
            </div>
            <div className={styles.footerLinks}>
              <div className={styles.linkColumn}>
                <h4 className={styles.linkTitle}>Services</h4>
                <ul className={styles.linkList}>
                  <li><a href="https://bürgerstrom.tirol/energiegemeinschaften/vermarkten" className={styles.footerLink}>EEG Vermarkten</a></li>
                  <li><a href="https://bürgerstrom.tirol/energiegemeinschaften/gründen" className={styles.footerLink}>EEG Gründen</a></li>
                  <li><a href="https://bürgerstrom.tirol/energiegemeinschaften/beitreten" className={styles.footerLink}>EEG Beitreten</a></li>
                  <li><a href="https://bürgerstrom.tirol/energiegemeinschaften/verwalten" className={styles.footerLink}>EEG Verwalten</a></li>
                </ul>
              </div>
              <div className={styles.linkColumn}>
                <h4 className={styles.linkTitle}>Über uns</h4>
                <ul className={styles.linkList}>
                  <li><a href="https://bürgerstrom.tirol/buergerstrom/so-funktionierts" className={styles.footerLink}>So funktioniert's</a></li>
                  <li><a href="https://bürgerstrom.tirol/buergerstrom/energiegemeinschaften" className={styles.footerLink}>Energiegemeinschaften</a></li>
                  <li><a href="https://bürgerstrom.tirol/buergerstrom/mitglied-werden" className={styles.footerLink}>Mitglied werden</a></li>
                </ul>
              </div>
              <div className={styles.linkColumn}>
                <h4 className={styles.linkTitle}>Kontakt</h4>
                <ul className={styles.linkList}>
                  <li><a href="https://bürgerstrom.tirol/kontakt" className={styles.footerLink}>Kontakt aufnehmen</a></li>
                  <li><a href="tel:051240506070" className={styles.footerLink}>0512 40 50 60 70</a></li>
                  <li><a href="mailto:kontakt@buergerstrom.tirol" className={styles.footerLink}>kontakt@buergerstrom.tirol</a></li>
                  <li><a href="https://bürgerstrom.tirol/termin" className={styles.footerLink}>Termin buchen</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <div className={styles.footerCopyright}>
              <p>© {new Date().getFullYear()} Bürgerstrom - Ein Projekt von mission solar</p>
              <p className={styles.footerSubtext}>Daten: E-Control Austria, Stand: Januar 2025</p>
            </div>
            <div className={styles.footerLegal}>
              <a href="https://bürgerstrom.tirol/legal/datenschutz" className={styles.legalLink}>Datenschutz</a>
              <a href="https://bürgerstrom.tirol/legal/impressum" className={styles.legalLink}>Impressum</a>
              <a href="https://bürgerstrom.tirol/legal/nutzungsbedingungen" className={styles.legalLink}>Nutzungsbedingungen</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnalyseAnfrageModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
}